/**
 * i18next initialisation for DJ SPY Official.
 *
 * Contract:
 *   • English (`en`) is the default and canonical fallback.
 *   • Arabic (`ar`) is opt-in via the `/ar/*` URL prefix. URL is source-of-truth.
 *   • Feature flag `REACT_APP_ENABLE_AR_LOCALE` gates the language switcher &
 *     `/ar/*` route matching. When disabled, `/ar/*` behaves as 404 / fallback.
 *   • Persistence: last chosen language stored in localStorage under
 *     `djspy_lang`. Direct hits to `/ar/*` always force ar regardless of the
 *     stored preference (URL wins).
 *   • Browser language detection is intentionally NOT used to auto-redirect
 *     first-time visitors — English default per user requirements.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import commonEN from "./locales/en/common.json";
import commonAR from "./locales/ar/common.json";

export const SUPPORTED_LANGS = ["en", "ar"];
export const DEFAULT_LANG = "en";
export const STORAGE_KEY = "djspy_lang";
export const AR_LOCALE_ENABLED =
  (process.env.REACT_APP_ENABLE_AR_LOCALE || "true").toLowerCase() !== "false";

function initialLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  // URL wins — /ar/* always forces ar on first render
  const path = window.location.pathname || "";
  if (AR_LOCALE_ENABLED && /^\/ar(\/|$)/.test(path)) return "ar";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  } catch { /* ignore quota / private mode */ }
  return DEFAULT_LANG;
}

if (!i18n.isInitialized) {
  i18n
    .use(initReactI18next)
    .init({
      resources: {
        en: { common: commonEN },
        ar: { common: commonAR },
      },
      lng: initialLang(),
      fallbackLng: DEFAULT_LANG,
      defaultNS: "common",
      ns: ["common"],
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      returnEmptyString: false,
    });
}

export default i18n;
